// Interactive charts for every M-SMART summary page (salesperson, manager, team, owner).
// Give it the /stats/* response and the selected date range; it works out the rest.
//
// Layout (kept deliberately quiet):
//   [Group by: Day | Week | Month]                       <- one control for every chart
//   Activity trend (full width; metric chips are the legend)
//   Sales vs target            | Conversion (pipeline + where leads ended up)
//   Team leaderboard (team views only)
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  Filler,
  Tooltip,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import {
  ACTIVITY_METRICS,
  PRESETS,
  autoGrain,
  bucketDates,
  dateList,
  metricTotal,
  money,
  presetRange,
  salesLeaf,
  totalsByMember,
  valueOn,
} from "./summaryData";

// Controllers registered explicitly: the sales chart mixes bar + line in one chart
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, BarController, LineController, Filler, Tooltip);

// Recessive chrome: text in ink tokens, faint grid
const INK = "#0b0b0b";
const INK_2 = "#52514e";
const GRID = "rgba(11,11,11,0.06)";
const SURFACE = "#ffffff";
const TARGET = "#52514e";

const baseScales = (isMoney = false) => ({
  x: { grid: { display: false }, ticks: { color: INK_2, maxRotation: 0, autoSkip: true, autoSkipPadding: 14 }, border: { color: GRID } },
  y: {
    beginAtZero: true,
    grid: { color: GRID },
    border: { display: false },
    ticks: { color: INK_2, precision: 0, maxTicksLimit: 5, callback: isMoney ? (v) => `RM ${Number(v).toLocaleString("en-MY")}` : undefined },
  },
});

const tooltipOpts = { backgroundColor: SURFACE, titleColor: INK, bodyColor: INK, borderColor: "rgba(11,11,11,0.15)", borderWidth: 1, padding: 10, boxPadding: 4, usePointStyle: true };

// Flat panel: thin border, no shadow (the page is already a card)
function Panel({ title, right, children, footer }) {
  return (
    <div className="h-100 border rounded-3 bg-white text-start p-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <h6 className="mb-0 fw-semibold">{title}</h6>
        {right}
      </div>
      {children}
      {footer ? <div className="text-muted mt-2" style={{ fontSize: "0.78rem" }}>{footer}</div> : null}
    </div>
  );
}

function Segmented({ value, options, onChange, ariaLabel }) {
  return (
    <div className="btn-group btn-group-sm" role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          title={o.title}
          className={`btn ${value === o.value ? "btn-dark" : "btn-outline-secondary"}`}
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const Empty = ({ text = "No activity in this date range.", height = 220 }) => (
  <div className="d-flex align-items-center justify-content-center text-muted" style={{ height, fontSize: "0.88rem" }}>
    {text}
  </div>
);

// ---------------------------------------------------------------------------
// Summary / Analysis tabs. The active tab lives in the URL (?view=analysis) so it
// survives a refresh and can be linked to; the date range & data are shared.
// ---------------------------------------------------------------------------
export function useSummaryTab() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("view") === "analysis" ? "analysis" : "summary";
  const setTab = useCallback(
    (next) => {
      const p = new URLSearchParams(params);
      if (next === "analysis") p.set("view", "analysis");
      else p.delete("view");
      setParams(p, { replace: true });
    },
    [params, setParams]
  );
  return [tab, setTab];
}

// Summary + Analysis as items of the page's existing top nav (nav-tabs), so no extra row.
// Other M-SMART pages just link to `<summary path>?view=analysis`.
export function SummaryNavItems({ tab, onChange }) {
  const item = (key, label) => (
    <li className="nav-item" key={key}>
      <button
        type="button"
        className={`nav-link ${tab === key ? "active" : ""}`}
        aria-current={tab === key ? "page" : undefined}
        onClick={() => onChange(key)}
      >
        {label}
      </button>
    </li>
  );
  return (
    <>
      {item("summary", "Summary")}
      {item("analysis", "Analysis")}
    </>
  );
}

// ---------------------------------------------------------------------------
// Quick date-range buttons. onPick receives [startDate, endDate].
// ---------------------------------------------------------------------------
export function RangePresets({ onPick, disabled }) {
  return (
    <div className="msm-presets d-flex flex-wrap gap-1 mt-2">
      {PRESETS.map((p) => (
        <button
          key={p.key}
          type="button"
          className="btn btn-sm btn-outline-secondary rounded-pill"
          disabled={disabled}
          onClick={() => onPick(presetRange(p.key))}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity trend. Metric chips double as the legend (no separate legend box).
// Click a point to see that period's full breakdown.
// ---------------------------------------------------------------------------
function ActivityTrend({ summary, buckets }) {
  const [selected, setSelected] = useState(["leads", "fuDone", "closed"]);
  const [kind, setKind] = useState("line");
  const [picked, setPicked] = useState(null);
  useEffect(() => setPicked(null), [buckets]); // new grouping/range -> old index means a different period

  const series = useMemo(
    () =>
      ACTIVITY_METRICS.map((m) => {
        const cbd = m.get(summary);
        return { ...m, values: buckets.map((b) => b.dates.reduce((t, d) => t + valueOn(cbd, d), 0)) };
      }),
    [summary, buckets]
  );

  const visible = series.filter((s) => selected.includes(s.key));
  const hasData = visible.some((s) => s.values.some((v) => v > 0));
  const tooShortForLine = buckets.length < 3; // a line through 1-2 points says nothing
  const asBar = kind === "bar" || tooShortForLine;

  const toggle = (key) => {
    setPicked(null);
    setSelected((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  };

  const data = {
    labels: buckets.map((b) => b.label),
    datasets: visible.map((s) => ({
      label: s.label,
      data: s.values,
      borderColor: s.color,
      backgroundColor: s.color,
      borderWidth: 2,
      borderDash: s.dashed ? [6, 4] : undefined,
      pointRadius: buckets.length > 45 ? 0 : 3,
      pointHoverRadius: 6,
      pointBackgroundColor: s.color,
      pointBorderColor: SURFACE,
      pointBorderWidth: 2,
      cubicInterpolationMode: "monotone", // smooth, never dips below 0
      borderRadius: 4,
      borderSkipped: "bottom",
      maxBarThickness: 24,
      categoryPercentage: 0.7,
      barPercentage: 0.9,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: { legend: { display: false }, tooltip: tooltipOpts },
    scales: baseScales(false),
    onClick: (_, els) => { if (els.length) setPicked(els[0].index); },
    onHover: (e, els) => { if (e?.native?.target) e.native.target.style.cursor = els.length ? "pointer" : "default"; },
  };

  const Chart = asBar ? Bar : Line;
  const pickedBucket = picked !== null ? buckets[picked] : null;

  return (
    <Panel
      title="Activity trend"
      right={
        tooShortForLine ? null : (
          <Segmented
            ariaLabel="Chart type"
            value={kind}
            onChange={setKind}
            options={[
              { value: "line", label: <i className="bi bi-graph-up" />, title: "Line" },
              { value: "bar", label: <i className="bi bi-bar-chart-line" />, title: "Bars" },
            ]}
          />
        )
      }
      footer={tooShortForLine ? "Pick 7 days or longer to see a trend." : pickedBucket ? null : "Click a point for that period's breakdown."}
    >
      {/* chips = legend + toggles, one scrollable row */}
      <div className="msm-scroll-x d-flex gap-1 mb-2 pb-1" style={{ overflowX: "auto", whiteSpace: "nowrap" }}>
        {series.map((s) => {
          const on = selected.includes(s.key);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => toggle(s.key)}
              className={`btn btn-sm rounded-pill border ${on ? "bg-light" : "bg-white text-muted"}`}
              aria-pressed={on}
              style={{ fontSize: "0.78rem", flex: "0 0 auto" }}
            >
              <span
                className="d-inline-block rounded-circle me-1 align-middle"
                style={{ width: 8, height: 8, background: on ? s.color : "transparent", border: `2px solid ${s.color}` }}
              />
              {s.label} <span className="fw-semibold ms-1">{metricTotal(summary, s)}</span>
            </button>
          );
        })}
      </div>

      {visible.length === 0 ? (
        <Empty text="Pick at least one metric above." height={240} />
      ) : !hasData ? (
        <Empty height={240} />
      ) : (
        <div style={{ height: 240 }}>
          <Chart data={data} options={options} />
        </div>
      )}

      {pickedBucket ? (
        <div className="border-top mt-2 pt-2">
          <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: "0.85rem" }}>
            <span className="fw-semibold">
              {pickedBucket.dates.length > 1 ? `${pickedBucket.dates[0]} → ${pickedBucket.dates[pickedBucket.dates.length - 1]}` : pickedBucket.dates[0]}
            </span>
            <button type="button" className="btn btn-sm btn-link text-muted p-0" onClick={() => setPicked(null)}>Close</button>
          </div>
          <div className="d-flex flex-wrap gap-3" style={{ fontSize: "0.82rem" }}>
            {series.map((s) => (
              <span key={s.key} className="text-nowrap">
                <span className="d-inline-block rounded-circle me-1" style={{ width: 7, height: 7, background: s.color }} />
                <span className="text-muted">{s.label}</span> <span className="fw-semibold">{s.values[picked]}</span>
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Sales vs target (both RM, one axis). Running total by day, or per group-by period.
// ---------------------------------------------------------------------------
function SalesVsTarget({ salesGap, dates, buckets }) {
  const [mode, setMode] = useState("cumulative");
  const useBuckets = useMemo(() => (mode === "cumulative" ? bucketDates(dates, "day") : buckets), [mode, dates, buckets]);
  const cbd = salesGap?.countByDate;

  const actual = useBuckets.map((b) => b.dates.reduce((t, d) => t + valueOn(cbd, d, salesLeaf.actual), 0));
  const target = useBuckets.map((b) => b.dates.reduce((t, d) => t + valueOn(cbd, d, salesLeaf.target), 0));
  const run = (arr) => arr.reduce((acc, v, i) => [...acc, (acc[i - 1] || 0) + v], []);
  const a = mode === "cumulative" ? run(actual) : actual;
  const t = mode === "cumulative" ? run(target) : target;

  const totalActual = Number(salesGap?.actualSalesAmount) || 0;
  const totalTarget = Number(salesGap?.targetAmount) || 0;
  const pct = totalTarget ? Math.round((totalActual / totalTarget) * 100) : null;
  const hasData = a.some((v) => v > 0) || t.some((v) => v > 0);

  const data = {
    labels: useBuckets.map((b) => b.label),
    datasets: [
      mode === "cumulative"
        ? { type: "line", label: "Actual", data: a, borderColor: "#2a78d6", backgroundColor: "rgba(42,120,214,0.10)", fill: true, borderWidth: 2, pointRadius: 0, pointHoverRadius: 6, cubicInterpolationMode: "monotone", order: 2 }
        : { type: "bar", label: "Actual", data: a, backgroundColor: "#2a78d6", borderRadius: 4, borderSkipped: "bottom", maxBarThickness: 24, order: 2 },
      { type: "line", label: "Target", data: t, borderColor: TARGET, backgroundColor: TARGET, borderDash: [6, 4], borderWidth: 2, pointRadius: 0, pointHoverRadius: 5, tension: 0, order: 1 },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false }, // legend is the key line under the title
      tooltip: { ...tooltipOpts, callbacks: { label: (c) => ` ${c.dataset.label}: ${money(c.parsed.y)}` } },
    },
    scales: baseScales(true),
  };

  return (
    <Panel
      title="Sales vs target"
      right={
        <Segmented
          ariaLabel="Sales view"
          value={mode}
          onChange={setMode}
          options={[{ value: "cumulative", label: "Running total" }, { value: "period", label: "Per period" }]}
        />
      }
    >
      <div className="d-flex align-items-baseline gap-2 mb-1">
        <span className="fs-5 fw-bold">{money(totalActual)}</span>
        <span className="text-muted" style={{ fontSize: "0.85rem" }}>
          {totalTarget ? <>of {money(totalTarget)} · <span className={pct >= 100 ? "text-success fw-semibold" : "fw-semibold text-dark"}>{pct}%</span></> : "no target set"}
        </span>
      </div>
      <div className="d-flex gap-3 mb-2 text-muted" style={{ fontSize: "0.78rem" }}>
        <span><span className="d-inline-block me-1 align-middle" style={{ width: 14, height: 3, background: "#2a78d6" }} />Actual</span>
        <span><span className="d-inline-block me-1 align-middle" style={{ width: 14, borderTop: `2px dashed ${TARGET}` }} />Target</span>
      </div>
      {!hasData ? <Empty text="No sales or targets in this date range." /> : (
        <div style={{ height: 220 }}>
          <Line data={data} options={options} />
        </div>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Conversion: pipeline stages + where leads ended up, as plain HTML bars
// (replaces two separate charts; numbers are always visible).
// ---------------------------------------------------------------------------
function Conversion({ summary }) {
  const stages = [
    { label: "Leads added", v: Number(summary?.totalLeadsAdded?.total) || 0, color: "#86b6ef" },
    { label: "Presented", v: Number(summary?.totalPresentations?.total) || 0, color: "#3987e5" },
    { label: "Booking", v: Number(summary?.finalStatus?.Booking?.total) || 0, color: "#1c5cab" },
    { label: "Closed", v: Number(summary?.finalStatus?.Closed?.total) || 0, color: "#104281" },
  ]; // lead counts only; sequential blue = ordered stages
  const top = Math.max(1, ...stages.map((s) => s.v));
  const base = stages[0].v;

  const outcomes = [
    { label: "Follow Up", v: Number(summary?.finalStatus?.["Follow Up"]?.total) || 0, color: "#1baf7a" },
    { label: "Booking", v: Number(summary?.finalStatus?.Booking?.total) || 0, color: "#eda100" },
    { label: "Closed", v: Number(summary?.finalStatus?.Closed?.total) || 0, color: "#008300" },
    { label: "Rejected", v: Number(summary?.finalStatus?.Rejected?.total) || 0, color: "#e34948" },
  ];
  const outTotal = outcomes.reduce((t, o) => t + o.v, 0);
  const hasData = stages.some((s) => s.v > 0) || outTotal > 0;

  return (
    <Panel title="Conversion">
      {!hasData ? <Empty /> : (
        <>
          <div className="mb-3">
            {stages.map((s) => (
              <div key={s.label} className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: "0.85rem" }}>
                <span className="text-muted" style={{ width: 90, flex: "0 0 auto" }}>{s.label}</span>
                <div className="flex-grow-1 rounded-1" style={{ background: "#f0efec", height: 10 }}>
                  <div className="rounded-1" style={{ width: `${(s.v / top) * 100}%`, minWidth: s.v ? 4 : 0, height: "100%", background: s.color }} />
                </div>
                <span className="fw-semibold text-end" style={{ width: 34 }}>{s.v}</span>
                <span className="text-muted text-end" style={{ width: 42 }}>{base ? `${Math.round((s.v / base) * 100)}%` : "–"}</span>
              </div>
            ))}
          </div>

          <div className="text-muted mb-1" style={{ fontSize: "0.78rem" }}>Status changes ({outTotal})</div>
          {outTotal ? (
            <>
              <div className="d-flex rounded-1 overflow-hidden mb-2" style={{ height: 10, gap: 2 }} aria-hidden="true">
                {outcomes.filter((o) => o.v).map((o) => (
                  <div key={o.label} title={`${o.label}: ${o.v}`} style={{ width: `${(o.v / outTotal) * 100}%`, background: o.color }} />
                ))}
              </div>
              <div className="d-flex flex-wrap gap-3" style={{ fontSize: "0.8rem" }}>
                {outcomes.map((o) => (
                  <span key={o.label} className="text-nowrap">
                    <span className="d-inline-block rounded-1 me-1" style={{ width: 8, height: 8, background: o.color }} />
                    {o.label} <span className="fw-semibold">{o.v}</span>
                    <span className="text-muted"> · {Math.round((o.v / outTotal) * 100)}%</span>
                  </span>
                ))}
              </div>
            </>
          ) : <div className="text-muted" style={{ fontSize: "0.82rem" }}>No status changes.</div>}
        </>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Leaderboard for team/manager summaries. Click a bar to open that member.
// ---------------------------------------------------------------------------
const LEADERBOARD_METRICS = [
  ...ACTIVITY_METRICS.filter((m) => m.key !== "repeat"),
  { key: "sales", label: "Actual Sales", color: "#2a78d6", isMoney: true },
];

function Leaderboard({ summary, dates, nameOf, onSelectMember }) {
  const [metricKey, setMetricKey] = useState("closed");
  const [showAll, setShowAll] = useState(false);
  const metric = LEADERBOARD_METRICS.find((m) => m.key === metricKey);

  const totals = useMemo(() => {
    if (metric.isMoney) return totalsByMember(summary?.salesGapSummary?.countByDate, dates, salesLeaf.actual);
    return totalsByMember(metric.get(summary), dates);
  }, [summary, dates, metric]);

  // include members with zero so nobody silently disappears
  const allMembers = new Set([...Object.keys(totals), ...Object.keys(summary?.totalLeadsAdded?.countByDate || {})]);
  const rows = [...allMembers].map((u) => ({ u, v: totals[u] || 0 })).sort((a, b) => b.v - a.v);
  const shown = showAll ? rows : rows.slice(0, 8);
  if (!rows.length) return null;

  const data = {
    labels: shown.map((r) => nameOf?.(r.u) || r.u),
    datasets: [{ label: metric.label, data: shown.map((r) => r.v), backgroundColor: metric.color, borderRadius: 4, borderSkipped: "start", maxBarThickness: 16 }],
  };
  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { ...tooltipOpts, callbacks: { label: (c) => ` ${metric.label}: ${metric.isMoney ? money(c.parsed.x) : c.parsed.x}` } },
    },
    scales: {
      x: { beginAtZero: true, grid: { color: GRID }, border: { display: false }, ticks: { color: INK_2, precision: 0, maxTicksLimit: 6, callback: metric.isMoney ? (v) => `RM ${Number(v).toLocaleString("en-MY")}` : undefined } },
      y: { grid: { display: false }, border: { display: false }, ticks: { color: INK } },
    },
    onClick: (_, els) => { if (els.length && onSelectMember) onSelectMember(shown[els[0].index].u); },
    onHover: (e, els) => { if (e?.native?.target) e.native.target.style.cursor = els.length && onSelectMember ? "pointer" : "default"; },
  };

  return (
    <Panel
      title="Team leaderboard"
      right={
        <select className="form-select form-select-sm" style={{ width: "auto" }} value={metricKey} onChange={(e) => setMetricKey(e.target.value)} aria-label="Leaderboard metric">
          {LEADERBOARD_METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
      }
      footer={onSelectMember ? "Click a member to open their own summary." : null}
    >
      <div style={{ height: Math.max(120, shown.length * 28 + 24) }}>
        <Bar data={data} options={options} />
      </div>
      {rows.length > 8 ? (
        <button type="button" className="btn btn-sm btn-link px-0" onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Show top 8" : `Show all ${rows.length}`}
        </button>
      ) : null}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// The Analysis tab.
//   summary         /stats/* response
//   start, end      Date objects of the selected range
//   nameOf          (username) => display name   (team views)
//   onSelectMember  (username) => void           (team views; optional)
//   showLeaderboard true on team/manager-wide views
// ---------------------------------------------------------------------------
export default function SummaryCharts({ summary, start, end, nameOf, onSelectMember, showLeaderboard = false }) {
  const dates = useMemo(() => dateList(start, end), [start, end]);
  const [grainChoice, setGrainChoice] = useState(null); // null = automatic from range length
  const grain = grainChoice || autoGrain(dates.length);
  const buckets = useMemo(() => bucketDates(dates, grain), [dates, grain]);
  if (!summary) return null;

  return (
    <div className="text-start">
      <div className="d-flex align-items-center gap-2 mb-2">
        <span className="text-muted" style={{ fontSize: "0.82rem" }}>Group by</span>
        <Segmented
          ariaLabel="Group by"
          value={grain}
          onChange={setGrainChoice}
          options={[{ value: "day", label: "Day" }, { value: "week", label: "Week" }, { value: "month", label: "Month" }]}
        />
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12">
          <ActivityTrend summary={summary} buckets={buckets} />
        </div>
        <div className="col-12 col-lg-7">
          <SalesVsTarget salesGap={summary.salesGapSummary} dates={dates} buckets={buckets} />
        </div>
        <div className="col-12 col-lg-5">
          <Conversion summary={summary} />
        </div>
        {showLeaderboard ? (
          <div className="col-12">
            <Leaderboard summary={summary} dates={dates} nameOf={nameOf} onSelectMember={onSelectMember} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
